import openai from '../lib/openai';
import { 
  APIConnectionError,
  AuthenticationError,
  PermissionDeniedError,
  RateLimitError,
  InternalServerError
} from 'openai';

/**
 * Maps OpenAI API error types to user-friendly error messages.
 * @param {Error} error - The error object from OpenAI API.
 * @returns {Object} Error information with isInternal flag and message.
 */
function getErrorMessage(error) {
  if (error instanceof AuthenticationError) {
    return { isInternal: true, message: 'Invalid API key or authentication failed. Please check your OpenAI API key.' };
  } else if (error instanceof PermissionDeniedError) {
    return { isInternal: true, message: 'Quota exceeded or authorization failed. You may have exceeded your usage limits or do not have access to this resource.' };
  } else if (error instanceof RateLimitError) {
    return { isInternal: true, message: 'Rate limit exceeded. You are sending requests too quickly. Please wait a moment and try again.' };
  } else if (error instanceof InternalServerError) {
    return { isInternal: true, message: 'OpenAI service is currently unavailable. Please try again later.' };
  } else if (error instanceof APIConnectionError) {
    return { isInternal: true, message: 'Unable to connect to OpenAI service. Please check your API key and internet connection.' };
  } else {
    return { isInternal: false, message: error?.message || 'An unexpected error occurred. Please try again.' };
  }
}

/**
 * Analyzes pronunciation attempt and provides AI-generated feedback with phoneme-level analysis.
 * @param {string} lineText - The Shakespeare line being practiced.
 * @param {string} characterName - Name of the character speaking the line.
 * @param {string} playName - Name of the play.
 * @param {Blob} audioBlob - The recorded audio blob from student pronunciation attempt.
 * @returns {Promise<Object>} Structured feedback with scores, suggestions, phoneme analysis, and coaching.
 */
export async function analyzePronunciation(lineText, characterName, playName, audioBlob) {
  try {
    // First, transcribe the audio using Whisper
    const audioFile = new File([audioBlob], 'pronunciation.wav', { type: 'audio/wav' });
    
    const transcription = await openai?.audio?.transcriptions?.create({
      model: 'whisper-1',
      file: audioFile,
      language: 'en',
      response_format: 'verbose_json', // Get word-level timestamps
      timestamp_granularities: ['word']
    });

    const studentText = transcription?.text;
    const wordTimestamps = transcription?.words || [];

    // Now analyze the pronunciation using GPT-5 with enhanced structured output for phoneme analysis
    const analysisResponse = await openai?.chat?.completions?.create({
      model: 'gpt-5',
      messages: [
        {
          role: 'system',
          content: `You are an expert Shakespearean pronunciation coach and theater director with deep knowledge of phonetics and speech analysis. Analyze student pronunciation attempts with focus on:
1. Overall accuracy of pronunciation compared to the original text
2. Phoneme-level analysis - identify specific sounds that were mispronounced or unclear
3. Understanding of Shakespearean language, meter, and rhythm
4. Emotional expression and delivery
5. Word-by-word feedback on difficult pronunciations
6. Areas for improvement with actionable, specific feedback
7. Encouragement and personalized coaching tips

For phoneme analysis, focus on:
- Consonant clarity (especially archaic pronunciations)
- Vowel sounds (especially in Old English words)
- Stress patterns and emphasis
- Rhythm and meter of verse
- Elision and linking sounds

Provide constructive, supportive feedback that helps students improve their Shakespearean performance skills with specific, actionable suggestions at the phoneme and word level.`
        },
        {
          role: 'user',
          content: `Analyze this pronunciation attempt with phoneme-level detail:

**Play:** ${playName}
**Character:** ${characterName}
**Original Line:** "${lineText}"
**Student's Pronunciation:** "${studentText}"

${wordTimestamps?.length > 0 ? `**Word Timing Data:**
${wordTimestamps?.map(w => `${w?.word} (${w?.start?.toFixed(2)}s - ${w?.end?.toFixed(2)}s)`)?.join(', ')}` : ''}

Provide detailed feedback on pronunciation accuracy, phoneme-level corrections, expression, and specific areas for improvement.`
        }
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'pronunciation_feedback',
          schema: {
            type: 'object',
            properties: {
              overallScore: {
                type: 'number',
                description: 'Overall pronunciation score from 0-100'
              },
              accuracyScore: {
                type: 'number',
                description: 'Text accuracy score from 0-100'
              },
              expressionScore: {
                type: 'number',
                description: 'Emotional expression and delivery score from 0-100'
              },
              clarityScore: {
                type: 'number',
                description: 'Phoneme clarity and enunciation score from 0-100'
              },
              pronunciationQuality: {
                type: 'string',
                enum: ['excellent', 'good', 'fair', 'needs-improvement'],
                description: 'Overall quality rating'
              },
              phonemeAnalysis: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    word: { type: 'string', description: 'The word being analyzed' },
                    expectedPronunciation: { type: 'string', description: 'Correct pronunciation (IPA or phonetic spelling)' },
                    studentPronunciation: { type: 'string', description: 'How the student pronounced it' },
                    issue: { type: 'string', description: 'Specific phonetic issue identified' },
                    correction: { type: 'string', description: 'Detailed correction instruction' },
                    severity: { type: 'string', enum: ['critical', 'moderate', 'minor'] }
                  },
                  required: ['word', 'issue', 'correction', 'severity']
                },
                description: 'Phoneme-level analysis of pronunciation issues (3-6 items)'
              },
              strengths: {
                type: 'array',
                items: { type: 'string' },
                description: 'List of what the student did well (2-4 items)'
              },
              areasForImprovement: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    area: { type: 'string', description: 'Specific area needing improvement' },
                    suggestion: { type: 'string', description: 'Actionable suggestion for improvement' },
                    priority: { type: 'string', enum: ['high', 'medium', 'low'] }
                  },
                  required: ['area', 'suggestion', 'priority']
                },
                description: 'Specific areas needing improvement with actionable suggestions (2-4 items)'
              },
              rhythmAndMeter: {
                type: 'object',
                properties: {
                  score: { type: 'number', description: 'Rhythm and meter score 0-100' },
                  feedback: { type: 'string', description: 'Specific feedback on rhythm and meter' },
                  tips: { type: 'array', items: { type: 'string' }, description: '2-3 specific tips' }
                },
                required: ['score', 'feedback', 'tips']
              },
              coachingTips: {
                type: 'array',
                items: { type: 'string' },
                description: 'Personalized coaching recommendations (2-3 items)'
              },
              encouragement: {
                type: 'string',
                description: 'Positive, encouraging message tailored to student performance'
              },
              nextSteps: {
                type: 'array',
                items: { type: 'string' },
                description: 'Concrete next steps for practice (2-3 items)'
              }
            },
            required: [
              'overallScore',
              'accuracyScore',
              'expressionScore',
              'clarityScore',
              'pronunciationQuality',
              'phonemeAnalysis',
              'strengths',
              'areasForImprovement',
              'rhythmAndMeter',
              'coachingTips',
              'encouragement',
              'nextSteps'
            ],
            additionalProperties: false
          }
        }
      },
      reasoning_effort: 'high',
      verbosity: 'high'
    });

    const feedback = JSON.parse(analysisResponse?.choices?.[0]?.message?.content);

    // Create audio URL for waveform visualization
    const audioUrl = URL.createObjectURL(audioBlob);

    return {
      success: true,
      transcribedText: studentText,
      wordTimestamps,
      audioUrl,
      feedback
    };

  } catch (error) {
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error analyzing pronunciation:', error);
    }
    throw new Error(errorInfo.message);
  }
}

/**
 * Generates practice exercises based on pronunciation weaknesses.
 * @param {Array} areasForImprovement - Areas identified for improvement.
 * @param {string} lineText - The original line being practiced.
 * @returns {Promise<Object>} Personalized practice exercises.
 */
export async function generatePracticeExercises(areasForImprovement, lineText) {
  try {
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-5-mini',
      messages: [
        {
          role: 'system',
          content: 'You are a Shakespearean acting coach creating targeted practice exercises to improve specific pronunciation and performance skills.'
        },
        {
          role: 'user',
          content: `Based on these areas for improvement: ${areasForImprovement?.map(a => a?.area)?.join(', ')}

Original line: "${lineText}"

Create 3 specific practice exercises that target these weaknesses.`
        }
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'practice_exercises',
          schema: {
            type: 'object',
            properties: {
              exercises: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    title: { type: 'string' },
                    description: { type: 'string' },
                    steps: {
                      type: 'array',
                      items: { type: 'string' }
                    },
                    focusArea: { type: 'string' }
                  },
                  required: ['title', 'description', 'steps', 'focusArea']
                },
                minItems: 3,
                maxItems: 3
              }
            },
            required: ['exercises'],
            additionalProperties: false
          }
        }
      },
      reasoning_effort: 'medium',
      verbosity: 'medium'
    });

    return JSON.parse(response?.choices?.[0]?.message?.content);

  } catch (error) {
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error generating practice exercises:', error);
    }
    throw new Error(errorInfo.message);
  }
}